import React, { useState } from 'react';
import GenericChart, { DEFAULT_HEIGHT, exportConfig, fontFamily } from '~/GenericChart/GenericChart';

import { roundedHumanFormat } from '~/utils/Price/Price';
import { BarSeriesValue, useSortingContext } from '../store/sortingContext';
import { ButtonSort } from './ButtonSort';
import { ERPApiResult } from '~/libs/infrastructure/schemas/erp';

type Scenario = {
	id: string | null;
	name: string;
};

interface ChartComparisonProps {
	parseErpResults: ERPApiResult;
	currency: string;
	leftModelResult: ERPApiResult;
	rightModelResult: ERPApiResult;
	scenarios: Scenario[];
	title?: string;
}

export const ChartComparison = ({
	parseErpResults,
	currency,
	leftModelResult,
	rightModelResult,
	title,
}: ChartComparisonProps) => {
	const { sortBy, handleSortChange, sortCategories, sortSeries } = useSortingContext();
	const [plotWidth, setPlotWidth] = useState<number>(0);

	const baseDataByCountries = Object.entries(parseErpResults.allDataByCountry).sort(([countryCodeA], [countryCodeB]) =>
		countryCodeA.localeCompare(countryCodeB)
	);

	const { profitDataSeries, leftDataSeries, lossDataSeries } = baseDataByCountries.reduce(
		(
			aggregator: {
				profitDataSeries: BarSeriesValue[];
				leftDataSeries: BarSeriesValue[];
				lossDataSeries: BarSeriesValue[];
			},
			[countryCode]
		) => {
			const countryValue = Number(rightModelResult.cumulativeStats[countryCode]?.revenue.value || 0);
			const valueToCompare = Number(leftModelResult.cumulativeStats[countryCode]?.revenue.value || 0);

			return {
				leftDataSeries: [...aggregator.leftDataSeries, [countryCode, valueToCompare || null] as BarSeriesValue],
				lossDataSeries: [
					...aggregator.lossDataSeries,
					[countryCode, countryValue < valueToCompare ? countryValue : null] as BarSeriesValue,
				],
				profitDataSeries: [
					...aggregator.profitDataSeries,
					[countryCode, countryValue > valueToCompare ? countryValue : null] as BarSeriesValue,
				],
			};
		},
		{ profitDataSeries: [], lossDataSeries: [], leftDataSeries: [] }
	);

	const seriesData = sortSeries(leftDataSeries);
	const categories = sortCategories(leftDataSeries, parseErpResults.stats);

	return (
		<div className='relative' style={{ minHeight: `${DEFAULT_HEIGHT}px` }}>
			<ButtonSort sortBy={sortBy} onChange={handleSortChange} offsetLeft={plotWidth} />
			<GenericChart
				setPlotWidth={setPlotWidth}
				options={{
					chart: {
						type: 'column',
						spacing: [10, 1, 0, 8],
						height: DEFAULT_HEIGHT,
						style: { position: 'inherit' },
					},

					colors: ['rgba(38, 41, 85, 0.8)'],

					series: [
						{
							type: 'column',
							name: 'Base',
							id: 'Base',
							pointPlacement: -0.1,
							data: seriesData,
						},
						{
							type: 'column',
							name: 'Profit',
							id: 'Profit',
							color: '#10d4b6',
							pointPlacement: 0.1,
							data: profitDataSeries,
						},
						{
							type: 'column',
							name: 'Loss',
							id: 'Loss',
							color: '#b91c1c',
							pointPlacement: 0.1,
							data: lossDataSeries,
						},
					],

					plotOptions: {
						series: {
							// @ts-expect-error: strange typing
							grouping: false,
							borderWidth: 0,
							states: {
								inactive: {
									opacity: 1,
								},
							},
						},
					},

					title: {
						text: title || '',
					},

					subtitle: {
						text: '',
					},

					xAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						lineWidth: 0,
						title: {
							text: 'Country',
							margin: 10,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
						type: 'category',
						categories,
						labels: {
							align: 'center',
							x: 0,
							style: {
								color: '#666',
								fontWeight: '500',
								fontSize: '11px',
								fontFamily,
							},
						},
					},
					yAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						min: 0,
						softMax: 1000,
						title: {
							text: `4Y revenue [${currency}]`,
							margin: 0,
							x: -12,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
					},

					exporting: exportConfig,

					legend: {
						enabled: false,
						align: 'left',
						verticalAlign: 'top',
						borderWidth: 0,
					},

					tooltip: {
						shared: true,
						borderRadius: 0,
						borderColor: '#000',
						backgroundColor: 'rgba(255,255,255,1)',
						shadow: false,
						useHTML: true,
						padding: 0,
						headerFormat: '',

						formatter: function (): string {
							return `
							<div style='background: #fff;padding: 0.33rem; border: 0.06rem solid #000; border-bottom_: none; width: calc(100% + 2px)' >
							<strong className="border-b border-gray-200" style="display:block;font-size:14px;margin-bottom:4px;padding-bottom:4px;color: #363972;">${
								parseErpResults.allDataByCountry[this.x || '']?.[0]?.country.name || ''
							}</strong>
							${
								this.points?.reduce(
									(str, point) =>
										`${str}
										<strong style="display:block;font-size:14px;margin-top:6px;margin-bottom:2px;color: ${point?.color}">${
											point.series.name
										}</strong>
										<strong style="display:block;margin-top:2px;font-size:12px;font-variant-numeric: tabular-nums;">${roundedHumanFormat(
											currency,
											String(point?.y)
										)}</strong>
										`,
									``
								) || ''
							}</div>`;
						},
						style: {
							color: '#000',
							fontSize: '12px',
							fontFamily,
						},
					},
				}}
			/>
		</div>
	);
};
